<template>
  <styled-wrap>
    <styled-payment-wrap
      v-if="!Object.keys(paymentMethods || {}).length && !searchData"
      class="stub demo container skeleton payment-wrap__items"
    >
      <div
        v-for="n in parseInt(12)"
        :key="n"
        class="post method payment-methods__item"
        :style="{ height: itemSize, width: itemSize }"
      >
        <div class="avatar icon" />
        <div class="line name" />
      </div>
    </styled-payment-wrap>

    <styled-empty-stub v-else-if="!Object.keys(paymentMethods || {}).length && searchData">{{
      $t('empty_data')
    }}</styled-empty-stub>

    <styled-payment-wrap v-else class="payment-wrap__items">
      <router-link
        v-for="method in paymentMethods"
        :id="method.code"
        :key="method.code"
        :to="getRoute(method)"
        :class="{
          'payment-methods__item': true,
          'payment-methods__item--disabled': isInDisabledMethods(method.code),
        }"
        :style="{ height: itemSize, width: itemSize }"
        @click.native="handleMethodSelect(method)"
      >
        <div v-if="method.tooltip" class="payment-methods__icon-wrap">
          <div
            v-if="method.tooltip"
            class="payment-methods__icon"
            :class="`payment-methods__icon--${method.color || 'grey'}`"
          >
            <tooltip v-if="method.tooltip" :data="method.tooltipData" :item-class="'right'" />
          </div>
        </div>

        <div class="payment_methods___logo-wrap">
          <div class="payment-methods__logo" :style="logoStyles(method)" />
        </div>
        <div class="payment-methods__text" />

        <div class="payment-methods__text">
          {{ method.name[($i18n || {}).locale || 'en'] || method.name['en'] || method.code }}
        </div>
      </router-link>
    </styled-payment-wrap>
  </styled-wrap>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import Tooltip from '@/components/UI/Tooltip';
import styled from 'vue-styled-components';
import f_maybe from '@/helpers/maybe';
import pc_f_compose from '@/helpers/compose';
import _ from 'lodash';

const styledEmptyStub = styled.section`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--on_background);
`;

const styledWrap = styled.section`
  flex: 1 1 0;
`;

const styledPaymentWrap = styled.section`
  display: flex;
  flex-wrap: wrap;
  padding-top: 10px;
  box-sizing: border-box;

  .payment-methods__item {
    background-color: var(--secondary_background_color);
    color: var(--primary_text_color);
    border: var(--border);
    border-radius: var(--border_radius);
    box-shadow: var(--box_shadow);
    border-color: var(--border_color);
    &:hover {
      box-shadow: var(--box_shadow_hover);
      outline: none;
    }
    &:active {
      box-shadow: var(--box_shadow_inset);
      background-color: var(--secondary_background_color);
    }
    &--disabled {
      opacity: 0.3;
      pointer-events: none;
    }

    cursor: pointer;
    transition: box-shadow 0.3s;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    flex-direction: column;
    position: relative;
    padding: 11px;
    margin: 10px 0 10px 20px;
    text-decoration: none;
    box-sizing: border-box;

    .payment-methods__icon-wrap {
      margin-left: auto;
      height: 21px;
      position: absolute;
      top: 10px;
      right: 10px;
      .payment-methods__icon {
        line-height: 1.25;
        padding: 3px 5px 0 4px;
        margin-bottom: 4px;
        border: solid 1px;
      }

      .payment-methods__icon--blue {
        color: #0055cc;
        border-color: rgba(0, 85, 204, 0.25);
      }

      .payment-methods__icon--green {
        color: #13bf4d;
        border-color: rgba(19, 191, 76, 0.25);
      }

      .payment-methods__icon--grey {
        color: #b4b5b8;
        border-color: rgba(215, 217, 220, 0.5);
      }
    }

    .payment-methods__text {
      color: var(--primary_text_color);
      font-size: 14px;
      width: 100%;
    }
  }

  .payment_methods___logo-wrap {
    width: 60%;
    height: 60%;

    .payment-methods__logo {
      width: 100%;
      height: 100%;
    }
  }

  .tooltip {
    display: none;
    top: 0;
    left: 172px;
    top: -10px;
    left: 35px;
  }

  .payment-methods__text {
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  .payment-methods__icon:hover .tooltip {
    display: block;
  }
`;

export default {
  filters: {
    capitalize(str) {
      return str[0].toUpperCase() + str.slice(1);
    },
  },
  components: {
    Tooltip,
    styledPaymentWrap,
    styledEmptyStub,
    styledWrap,
  },

  computed: {
    ...mapState([
      'services',
      'methods',
      'selectedCategory',
      'groupedCategories',
      'searchData',
      'categories',
    ]),
    ...mapGetters(['extendedMethods', 'isInDisabledMethods']),
    ...mapState('UI', ['customThemeOptions']),

    // Methods grouped by category
    currentMethods() {
      if (this.selectedCategory != 'all') {
        return this.groupedCategories[this.selectedCategory];
      } else {
        return this.methods;
      }
    },

    isMatchExpectation(element) {
      return val.toLowerCase.indexOf(expected.toLowerCase) !== -1;
    },
    /**
     * Here counting entities of services, fee of methods, and set tooltip data
     */
    paymentMethods() {
      const data = [];
      let currentMethods = [];
      if (this.currentMethods) {
        currentMethods = Object.values(this.currentMethods) || [];
      }

      let methodsToShow = f_maybe(currentMethods)(m =>
        m.map(pc_f_compose(this.markAutoProcess, this.convertToUIFormat))
      ).f_result();

      // TODO: refactor to one line to chain above
      if (this.searchData) {
        /**
         * https://lodash.com/docs/4.17.11#compact
         * Creates an array with all falsey values removed.
         * The values false, null, 0, "", undefined, and NaN are falsey.
         */
        methodsToShow = _.compact(
          methodsToShow.map(m => {
            if (f_maybe(Object.values(m.name))(this.filterByValue).f_result().length) {
              return m;
            }
          })
        );
      }

      return methodsToShow;
    },

    itemSize() {
      let size = '183.3px';

      if (this.screenSize.width > 1050 && this.screenSize.width <= 1240) {
        size = `${(this.screenSize.width - 140) / 6}px`;
      } else if (this.screenSize.width > 850 && this.screenSize.width <= 1050) {
        size = `${(this.screenSize.width - 120) / 5}px`;
      } else if (this.screenSize.width > 733 && this.screenSize.width <= 850) {
        size = `${(this.screenSize.width - 100) / 4}px`;
      } else if (this.screenSize.width > 557 && this.screenSize.width <= 734) {
        size = `${(this.screenSize.width - 80) / 3}px`;
      } else if (this.screenSize.width <= 557) {
        size = `${(this.screenSize.width - 60) / 2}px`;
      }

      return size;
    },

    methodList() {
      const data = [];
      return data;
    },
  },

  created() {
    console.log('Is some available services', this.calculateAvailableServices());

    // If data was already loaded and app initialized, check for available sevices
    // TODO: create flag that indicates everything are ready;
    if (this.paymentMethods.length) {
      if (!this.calculateAvailableServices()) {
        this.setGlobalError({
          status: 'no_availables_services',
          code: 'no_availables_services',
          status_code: 'no_availables_services',
        });
      }
    }
    if (this.categories) {
      const navigationData = this.categories.map(el => {
        let newEl = {};
        newEl.callback = 'category';
        newEl.name = el;
        return newEl;
      });
      this.setTabsNavigationData(navigationData);
    }
  },

  methods: {
    ...mapActions(['selectMethod', 'setTabsNavigationData', 'setGlobalError']),

    countAvailableServices(method) {
      const { code } = method;
      console.log('Method name', name);
    },

    /**
     *  @returns {Array} array of filtered method names based on search string
     */
    filterByValue(arr) {
      return arr.filter(n => n.toLowerCase().indexOf(this.searchData.toLowerCase()) !== -1);
    },

    /**
     * @returns {Object} for payment method grid item
     */
    convertToUIFormat({
      name = '',
      code = ' ',
      logo = '',
      icon = null,
      tooltip = false,
      tooltipData = {},
    }) {
      return {
        name,
        code,
        // Good night, ESLINT
        logo:
          (this.customThemeOptions || {}).show_method_logo &&
          (this.customThemeOptions || {}).show_method_logo.toLowerCase() === 'true'
            ? logo
            : icon
            ? icon
            : logo,

        tooltip,
        tooltipData,
      };
    },

    /**
     * @returns {Object} for payment method grid item
     */
    filterBySearchData(method) {
      return method;
    },

    /**
     * @returns {Object} for payment method grid item with autoprocess flag //
     */

    markAutoProcess(method) {
      // If only 1 service, and flow hpp this service might be autoprocessed;

      const services = this.services[method.code] || [];
      if (services && services.length && services.length == 1) {
        if (services[0].flow === 'hpp' && services[0].fields && services[0].fields.length === 0) {
          method.autorocess = true;
          method.tooltip = true;
          method.tooltipData = {
            title: 'direct_call_forwarding',
            description: 'direct_call_forwarding_description',
          };
        }
      }

      return { ...method };
    },

    handleMethodSelect(method) {
      this.selectMethod(method);
    },

    calculateAvailableServices() {
      return this.paymentMethods.some(method => {
        return !this.isInDisabledMethods(method.code);
      });
    },

    getRoute(method) {
      let route = {
        name: 'pm',
        params: { pm: method.code },
        query: { 'payment-request': this.requestId },
      };

      return route;
    },

    // TODO: Make me CSS in JS, please
    logoStyles(method) {
      return {
        'background-image': `url(${method.logo})`,
        'background-repeat': 'no-repeat',
        'background-position': 'center',
        'background-size': 'contain',
      };
    },

    getIcon(name, methodData) {
      if (methodData.icon === null) {
        return `${window.configData.staticUrl}/assets/money.svg`;
      }

      let symbol = methodData.icon.indexOf('?') !== -1 ? '&' : '?';
      return `${methodData.icon}${symbol}w=130`;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Second ver */
@keyframes shine-lines {
  0% {
    background-position: -100px;
  }
  40%,
  100% {
    background-position: 140px;
  }
}
@keyframes shine-avatar {
  0% {
    background-position: -32px;
  }
  40%,
  100% {
    background-position: 208px;
  }
}

.skeleton {
  flex-wrap: wrap;

  .method .avatar + .line {
    margin-top: 11px;
    width: 100px;
  }
  .method .line ~ .line {
    background-color: #ddd;
  }
  .method {
    /* background: #d7d9dc; */
    border-radius: 0.4em;
    padding: 11px;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: space-evenly;
    .icon {
      width: 60%;
      height: 60%;
      border-radius: 0.4em;
      border-radius: 25%;
      background-image: linear-gradient(
        90deg,
        #f4f4f4 0px,
        rgba(229, 229, 229, 0.8) 40px,
        #f4f4f4 80px
      );
      background-size: 600px;
      animation: shine-avatar 2s infinite ease-out;
    }
    .line {
      width: 140px;
      height: 16px;
      border-radius: 7px;
      background-image: linear-gradient(
        90deg,
        #f4f4f4 0px,
        rgba(229, 229, 229, 0.8) 40px,
        #f4f4f4 80px
      );
      background-size: 600px;
      animation: shine-lines 2s infinite ease-out;
    }
  }
}
</style>
